import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHero from "@/components/PageHero";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trophy, UploadCloud, X, Clock, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const EVENT_OPTIONS = [
  { key: "mens_singles", label: "Men's Singles" },
  { key: "mens_doubles", label: "Men's Doubles" },
  { key: "womens_singles", label: "Women's Singles" },
  { key: "womens_doubles", label: "Women's Doubles" },
  { key: "mixed_doubles", label: "Mixed Doubles" },
];
const AGE_OPTIONS = [
  { key: "u_12", label: "U-12" }, { key: "u_14", label: "U-14" },
  { key: "u_10", label: "U-10" }, { key: "u_12", label: "U-12" }, { key: "u_14", label: "U-14" },
  { key: "u_16", label: "U-16" }, { key: "u_19", label: "U-19" },
  { key: "19+", label: "19+ (Open)" }, { key: "35+", label: "35+" },
  { key: "50+", label: "50+" }, { key: "60+", label: "60+" },
];

function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
}

function MultiSelectChips({ options, selected, onChange, testIdPrefix }) {
  const toggle = (k) => {
    onChange(selected.includes(k) ? selected.filter((s) => s !== k) : [...selected, k]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = selected.includes(o.key);
        return (
          <button key={o.key} type="button" data-testid={`${testIdPrefix}-${o.key}`} onClick={() => toggle(o.key)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border rounded-sm transition-colors ${on ? "bg-jpa-primary text-white border-jpa-primary" : "bg-white text-jpa-ink border-jpa-border hover:border-jpa-primary"}`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function CreateTournamentForm({ onCreated }) {
  const [form, setForm] = useState({
    name: "", description: "", location: "", start_date: "", end_date: "",
    event_categories: [], age_categories: [], prize_pool: "", image_url: "",
    entry_fees: {}, payment_qr_data_url: null, upi_id: "", external_booking_url: "",
  });
  const [factsheet, setFactsheet] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const setFee = (ec, ac, val) => {
    const fees = { ...(form.entry_fees || {}) };
    fees[ec] = { ...(fees[ec] || {}) };
    fees[ec][ac] = parseInt(val, 10) || 0;
    setForm({ ...form, entry_fees: fees });
  };

  const onFactsheetUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Please upload a PDF"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("PDF must be under 5MB"); return; }
    setFactsheet({ name: f.name, dataUrl: await fileToDataUrl(f) });
  };

  const onQrUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("QR image must be under 2MB"); return; }
    setForm({ ...form, payment_qr_data_url: await fileToDataUrl(f) });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.event_categories.length === 0) { toast.error("Select at least one event category"); return; }
    if (form.age_categories.length === 0) { toast.error("Select at least one age category"); return; }
    setSubmitting(true);
    try {
      await api.post("/club/tournaments", { ...form, factsheet_data_url: factsheet?.dataUrl || null });
      toast.success("Tournament submitted for JPA approval");
      setForm({ name: "", description: "", location: "", start_date: "", end_date: "", event_categories: [], age_categories: [], prize_pool: "", image_url: "", entry_fees: {}, payment_qr_data_url: null, upi_id: "", external_booking_url: "" });
      setFactsheet(null);
      onCreated?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSubmitting(false); }
  };

  return (
    <form data-testid="club-create-tournament-form" onSubmit={submit} className="border border-jpa-border rounded-sm p-6 space-y-5">
      <div className="flex items-center gap-3">
        <Trophy className="w-5 h-5 text-jpa-primary" />
        <h3 className="font-display text-2xl tracking-wider">Create tournament</h3>
      </div>
      <p className="text-sm text-jpa-muted">JPA will review your submission and assign a division + tier at approval time.</p>

      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>Name *</Label><Input data-testid="ct-name" value={form.name} onChange={setField("name")} required className="mt-1 rounded-sm" /></div>
        <div><Label>Venue / location *</Label><Input data-testid="ct-location" value={form.location} onChange={setField("location")} required className="mt-1 rounded-sm" /></div>
        <div><Label>Start date *</Label><Input data-testid="ct-start" type="date" value={form.start_date} onChange={setField("start_date")} required className="mt-1 rounded-sm" /></div>
        <div><Label>End date *</Label><Input data-testid="ct-end" type="date" value={form.end_date} onChange={setField("end_date")} required className="mt-1 rounded-sm" /></div>
        <div><Label>Playing prize pool (optional)</Label><Input data-testid="ct-prize" value={form.prize_pool} onChange={setField("prize_pool")} placeholder="e.g. ₹1,00,000" className="mt-1 rounded-sm" /></div>
        <div>
          <Label>Poster image URL <span className="text-jpa-muted text-xs font-normal">(portrait 2:3, e.g. 800×1200)</span></Label>
          <Input data-testid="ct-image" value={form.image_url} onChange={setField("image_url")} placeholder="https://…" className="mt-1 rounded-sm" />
        </div>
        <div className="md:col-span-2">
          <Label>External booking URL <span className="text-jpa-muted text-xs font-normal">(optional — use for National/International events hosted elsewhere)</span></Label>
          <Input data-testid="ct-external-url" value={form.external_booking_url} onChange={setField("external_booking_url")} placeholder="https://aipa.example.com/register/…" className="mt-1 rounded-sm" />
          <div className="text-[11px] text-jpa-muted mt-1">If set, the JOIN NOW button will open this URL instead of the in-app registration wizard.</div>
        </div>
        <div className="md:col-span-2"><Label>Description</Label><Textarea data-testid="ct-description" value={form.description} onChange={setField("description")} rows={3} className="mt-1 rounded-sm" /></div>
      </div>

      <div>
        <Label>Event categories * (choose multiple)</Label>
        <div className="mt-2"><MultiSelectChips options={EVENT_OPTIONS} selected={form.event_categories} onChange={setField("event_categories")} testIdPrefix="ct-evt" /></div>
      </div>
      <div>
        <Label>Age categories * (choose multiple)</Label>
        <div className="mt-2"><MultiSelectChips options={AGE_OPTIONS} selected={form.age_categories} onChange={setField("age_categories")} testIdPrefix="ct-age" /></div>
      </div>

      {form.event_categories.length > 0 && form.age_categories.length > 0 && (
        <div>
          <Label>Entry fees per event × age (INR) *</Label>
          <div className="mt-2 border border-jpa-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-jpa-surface text-[10px] font-bold uppercase tracking-widest">
                  <th className="px-2 py-2 text-left">Event ↓ / Age →</th>
                  {form.age_categories.map((ac) => <th key={ac} className="px-2 py-2">{AGE_OPTIONS.find((o) => o.key === ac)?.label || ac}</th>)}
                </tr>
              </thead>
              <tbody>
                {form.event_categories.map((ec) => (
                  <tr key={ec} className="border-t border-jpa-border">
                    <td className="px-2 py-1 font-bold text-xs">{EVENT_OPTIONS.find((o) => o.key === ec)?.label || ec}</td>
                    {form.age_categories.map((ac) => (
                      <td key={ac} className="px-1 py-1">
                        <Input data-testid={`ct-fee-${ec}-${ac}`} type="number" min={0} value={(form.entry_fees?.[ec]?.[ac]) ?? ""} onChange={(e) => setFee(ec, ac, e.target.value)} placeholder="₹0" className="rounded-sm h-8 text-xs w-24" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <Label>Payment QR image (UPI QR — JPG/PNG)</Label>
        <label htmlFor="ct-qr" className="mt-1 flex items-center gap-3 border-2 border-dashed border-jpa-border rounded-sm p-4 cursor-pointer hover:border-jpa-primary transition-colors">
          <UploadCloud className="w-6 h-6 text-jpa-primary" />
          <span className="text-sm text-jpa-muted">{form.payment_qr_data_url ? "QR uploaded — click to replace" : "Click to upload UPI QR (up to 2MB)"}</span>
          <input id="ct-qr" data-testid="ct-qr-upload" type="file" accept="image/*" className="hidden" onChange={onQrUpload} />
        </label>
        {form.payment_qr_data_url && (
          <div className="mt-2 w-32 h-32 border border-jpa-border"><img src={form.payment_qr_data_url} alt="QR" className="w-full h-full object-cover" /></div>
        )}
        <div className="mt-3">
          <Label>UPI ID / VPA <span className="text-jpa-muted text-xs font-normal">(shown next to the QR — default: pla1447@fbl)</span></Label>
          <Input data-testid="ct-upi-id" value={form.upi_id} onChange={setField("upi_id")} placeholder="e.g. yourclub@upi" className="mt-1 rounded-sm font-mono max-w-md" />
        </div>
      </div>

      <div>
        <Label>Factsheet PDF (rules & regulations)</Label>
        <label htmlFor="ct-factsheet" className="mt-1 flex items-center gap-3 border-2 border-dashed border-jpa-border rounded-sm p-4 cursor-pointer hover:border-jpa-primary transition-colors">
          <UploadCloud className="w-6 h-6 text-jpa-primary" />
          <span className="text-sm text-jpa-muted">{factsheet ? factsheet.name : "Click to upload factsheet PDF (up to 5MB)"}</span>
          <input id="ct-factsheet" data-testid="ct-factsheet-upload" type="file" accept="application/pdf" className="hidden" onChange={onFactsheetUpload} />
        </label>
        {factsheet && (
          <div className="mt-2 inline-flex items-center gap-2 text-xs text-jpa-muted bg-jpa-surface px-3 py-1 rounded-sm">
            <span>{factsheet.name}</span>
            <button type="button" onClick={() => setFactsheet(null)}><X className="w-3 h-3" /></button>
          </div>
        )}
      </div>

      <Button data-testid="ct-submit" type="submit" disabled={submitting} className="bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest font-bold rounded-sm">
        <Plus className="w-4 h-4 mr-2" />{submitting ? "Submitting…" : "Submit for JPA approval"}
      </Button>
    </form>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { icon: Clock, label: "Awaiting JPA review", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    approved: { icon: CheckCircle2, label: "JPA Sanctioned", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    rejected: { icon: XCircle, label: "Not sanctioned", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  };
  const conf = map[status] || map.pending;
  const Icon = conf.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest border rounded-sm px-2 py-1 ${conf.cls}`}>
      <Icon className="w-3 h-3" /> {conf.label}
    </span>
  );
}

export default function ClubDashboardPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [club, setClub] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [tab, setTab] = useState("tournaments");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "club") { nav("/"); return; }
    Promise.all([api.get("/club/me"), api.get("/club/tournaments")])
      .then(([c, t]) => { setClub(c.data); setTournaments(t.data); })
      .catch(() => toast.error("Failed to load club data"));
  }, [user, loading, nav]);

  const reload = () => api.get("/club/tournaments").then((r) => setTournaments(r.data));

  const stats = useMemo(() => ({
    total: tournaments.length,
    pending: tournaments.filter((t) => t.approval_status === "pending").length,
    approved: tournaments.filter((t) => t.approval_status === "approved").length,
  }), [tournaments]);

  if (!club) return <div className="py-24 text-center text-jpa-muted">Loading…</div>;

  return (
    <div data-testid="club-dashboard-page">
      <PageHero eyebrow={`Club · ${club.district}`} title={club.name.toUpperCase()} subtitle="Manage your JPA affiliation and submit tournaments for sanctioning."
        image={club.logo_data_url || "https://images.unsplash.com/photo-1617144520113-88ae706a86eb?w=1800"} />

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: "Total tournaments", val: stats.total },
            { label: "Pending review", val: stats.pending },
            { label: "JPA sanctioned", val: stats.approved },
          ].map((s) => (
            <div key={s.label} className="border border-jpa-border rounded-sm p-5 bg-white">
              <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary">{s.label}</div>
              <div className="font-display text-4xl tracking-wider mt-1">{s.val}</div>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-jpa-surface rounded-sm h-auto p-1">
            <TabsTrigger data-testid="club-tab-tournaments" value="tournaments" className="uppercase tracking-widest text-xs font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-6 py-2">My tournaments</TabsTrigger>
            <TabsTrigger data-testid="club-tab-create" value="create" className="uppercase tracking-widest text-xs font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-6 py-2">+ New tournament</TabsTrigger>
          </TabsList>

          <TabsContent value="tournaments" className="mt-6">
            {tournaments.length === 0 ? (
              <div className="border border-dashed border-jpa-border rounded-sm p-16 text-center">
                <div className="text-jpa-muted mb-4">No tournaments yet.</div>
                <Button onClick={() => setTab("create")} className="bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest font-bold rounded-sm">
                  <Plus className="w-4 h-4 mr-2" /> Create your first tournament
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {tournaments.map((t) => (
                  <div key={t.id} data-testid={`club-tournament-${t.id}`} className="border border-jpa-border rounded-sm p-4 flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-bold uppercase text-sm">{t.name}</div>
                        <StatusPill status={t.approval_status} />
                        {t.division && <Badge variant="outline" className="text-[10px] uppercase tracking-widest rounded-sm">{t.division}</Badge>}
                        {t.tier != null && <Badge className="bg-jpa-primary text-white text-[10px] uppercase tracking-widest rounded-sm">Tier {t.tier}</Badge>}
                      </div>
                      <div className="text-xs text-jpa-muted mt-1">
                        {t.location} · {t.start_date} → {t.end_date}
                        {t.event_categories?.length ? ` · ${t.event_categories.length} event(s)` : ""}
                      </div>
                    </div>
                    <Link to={`/tournaments/${t.id}`} className="text-[11px] font-bold uppercase tracking-widest text-jpa-primary hover:text-jpa-primary-hover">View →</Link>
                    {t.approval_status === "approved" && (
                      <Link to={`/organizer/tournaments/${t.id}`} data-testid={`club-manage-${t.id}`} className="text-[11px] font-bold uppercase tracking-widest text-jpa-primary hover:text-jpa-primary-hover ml-3">Manage →</Link>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="create" className="mt-6">
            <CreateTournamentForm onCreated={() => { reload(); setTab("tournaments"); }} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
